// import _ from "lodash"
import * as React from "react";
import AvatarEditor from "react-avatar-editor";
import {
  Dialog,
  AppBar,
  Toolbar,
  Slide,
  createStyles,
  makeStyles,
  Theme,
  ButtonBase,
  FormControlLabel,
} from "@material-ui/core";
import { TransitionProps } from "@material-ui/core/transitions";

import "./style.scss";
import PictureEditor from "./PictureEditor";
import FirstUpload from "./FirstUpload";
import Loader from "../Loader/Loader";

import api from "../../utils/api";
import { appActions, userInfoActions } from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import {
  IEntry,
  IDocumentIds,
  IEntryDocuments,
} from "../../types/appDataTypes";
import * as AppTypes from "AppTypes";
import { addMessage } from "../MessageContainer";
import { resizePrimaryImage } from "../../utils";
const MAX_QUALITY_IMAGE = 0.9;
const TYPE_IMAGE = "image/jpeg";

interface IArtworkPictures {
  entryIndex: number;
  onIdRetrieved: (entryId: string) => void;
  onPhotoUpload?: (photos: IEntryDocuments) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: { position: "relative" },
    title: { marginLeft: theme.spacing(2), flex: 1 },
  })
);

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide {...props} direction="up" ref={ref} />;
});

const ArtworkPictures: React.FC<IArtworkPictures> = (props) => {
  const dispatch = useDispatch();
  const { entryIndex, onIdRetrieved, onPhotoUpload } = props;

  const { appId, entry, isCallingApi, pictureStore, langPack, artworkSizeLimit } = useSelector(
    (store: AppTypes.ReducerState) => ({
      appId: store.userInfo.appId || "",
      entry: store.userInfo.artEntry[entryIndex] || ({} as IEntry),
      isCallingApi: store.app.isCallingApi,
      pictureStore: store.app.pictureStore,
      langPack: store.config.langPack,
      artworkSizeLimit: store.config.appConfig.artworkSizeLimit,
    })
  );
 
  const pictures = pictureStore[entry.entryId] || {
    p1: null,
    s1: null,
    s2: null,
  };

  const classes = useStyles();
  const imageInputElem = React.useRef(null);
  const pictureEditorElement = React.useRef<AvatarEditor>(null);
  const canvasResizeProxy = React.useRef<HTMLCanvasElement>(null);
  const zoomInButton = React.useRef<HTMLButtonElement>(null);
  const zoomOutButton = React.useRef<HTMLButtonElement>(null);
  const rotateLeftButton = React.useRef<HTMLButtonElement>(null);
  const rotateRightButton = React.useRef<HTMLButtonElement>(null);

  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const [imageDialogIndex, setImageDialogIndex] =
    React.useState<IDocumentIds>("p1");
  const [imagePath, setImagePath] = React.useState("");
  const [isReadingImage, setReadingImage] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUploadError, setUploadError] = React.useState(false);
  const [imageUpdated, setImageUpdated] = React.useState(false);

  React.useEffect(() => {
    const body: HTMLBodyElement = document.querySelector("body");
    if (isDialogOpen) {
      body.setAttribute("data-lock", "true");
    } else {
      body.removeAttribute("data-lock");
    }
  }, [isDialogOpen]);

  const selectImageHandler = () => {
    if (!imageInputElem.current) return;
    imageInputElem.current.click();
    setUploadError(false);
  };

  const deleteImageHandler = async (documentId: "s1" | "s2") => {
    const response = await api.deleteEntryImage(
      appId,
      entry.entryId,
      documentId
    );
    if (response && response.status === 200) {
      dispatch(
        appActions.updateEntrySingleDocument(entry.entryId, documentId, "")
      );
      dispatch(appActions.setApplicationUpdated(true));
      onModalCloseHandler();
    }
  };

  const onImageChangeHandler = () => {
    const file = imageInputElem.current && imageInputElem.current.files[0];
    if (!file) return;
    if (file.size > artworkSizeLimit) {
      addMessage({
        title: langPack["errorUploadSizeTitle"],
        text: langPack["errorUploadSizeText"],
      });
      return;
    }

    setReadingImage(true);

    const reader = new FileReader();
    reader.onload = (event: any) => {
      setReadingImage(false);
      setImagePath(event.target.result as string);

      const validatorImage = new Image();
      validatorImage.src = "" + event.target.result;
      validatorImage.onerror = () => {
        addMessage({
          title: "Wrong file type",
          text: "Please select png or jpg file",
        });
        onModalCloseHandler();
      };

      validatorImage.onload = () => {
        setImageUpdated(true);
        setImagePath(event.target.result as string);
        setDialogOpen(true);
      };
    };
    reader.readAsDataURL(imageInputElem.current.files[0]);
  };

  const onModalCloseHandler = () => {
    setDialogOpen(false);
    imageInputElem.current.value = "";
    setImagePath("");
    onPhotoUpload(imageUpdated ? pictures : null);
  };

  const updateFileEntry = (
    file: File,
    documentId: IDocumentIds,
    entryId: string
  ) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      dispatch(
        appActions.updateEntrySingleDocument(
          entryId,
          documentId,
          reader.result as string
        )
      );
      onModalCloseHandler();
    };
  };

  const saveImage = async (file: File, documentId: IDocumentIds) => {
    let entryId = entry.entryId;
    if (!entryId) {
      const entryResponse = await api.createEntry(appId, entry);
      if (!(entryResponse && entryResponse.status === 200)) return;
      entryId = entryResponse.data.entryId;
      entry.entryId = entryId;
      onIdRetrieved(entryId);
      dispatch(userInfoActions.updateEntry(entry, entryIndex));
      updateFileEntry(file, documentId, entryId);
    } else {
      updateFileEntry(file, documentId, entryId);
    }
  };

  const uploadDocumentHandler = () => {
    if (isCallingApi) return;

    if (!pictureEditorElement) return;
    const avatarEditor = pictureEditorElement.current;
    const image = new Image();
    image.src = avatarEditor.props.image as string;

    image.onload = () => {
      resizePrimaryImage(
        image,
        canvasResizeProxy.current,
        avatarEditor.props.rotate
      ).toBlob(
        (blob) => {
          const file = new File([blob], "image.jpg", { type: TYPE_IMAGE });
          saveImage(file, imageDialogIndex);
        },
        TYPE_IMAGE,
        MAX_QUALITY_IMAGE
      );
    };
  };

  const PicturesRenderer = () => {
    return (
      <div className="image-display">
        {["p1"].map((d, key) => {
          const documentId = d as IDocumentIds;
          return (
            <div
              key={key}
              className={
                documentId === "p1"
                  ? "image-display--main"
                  : "image-display--sub"
              }
              onClick={() => {
                setImageDialogIndex(documentId);

                if (pictures[documentId]) {
                  setImagePath(pictures[documentId]);
                  setDialogOpen(true);
                } else {
                  selectImageHandler();
                }
              }}
              style={
                pictures[documentId]
                  ? {
                      backgroundImage: `url(${pictures[documentId]})`,
                      color: "transparent",
                    }
                  : {}
              }
            ></div>
          );
        })}

        <div className="image-display__construction" style={{ order: 10 }}>
         {langPack["clickImage"]}
        </div>
      </div>
    );
  };

  const isSaveDisabled =
    isCallingApi ||
    isReadingImage ||
    !(imageInputElem.current && imageInputElem.current.files[0]);

  return (
    <React.Fragment>
      {!pictures.p1 ? (
        <FirstUpload onClick={selectImageHandler} />
      ) : (
        <PicturesRenderer />
      )}

      <input
        className="hidden-upload"
        type="file"
        accept="image/*"
        ref={imageInputElem}
        onChange={onImageChangeHandler}
      />
      <Dialog
        container={document.querySelector("#dialogContainer")}
        fullScreen
        open={isDialogOpen}
        onClose={onModalCloseHandler}
        TransitionComponent={Transition}
        className={"dialog"}
      >
        <AppBar className={classes.appBar}>
          <Toolbar className="btn-wrapper">
            <ButtonBase
              className="btn-close"
              onClick={onModalCloseHandler}
              aria-label="close"
            >
              <img
                src="images/x.svg"
                alt=""
                style={{ width: "20px", height: "20px" }}
              />
            </ButtonBase>
            <ButtonBase
              disabled={isCallingApi}
              className="btn-save"
              // style={{ textTransform: "capitalize", opacity: isSaveDisabled ? 0 : 1 }}
              style={{ textTransform: "capitalize" }}
              onClick={uploadDocumentHandler}
            >
              {langPack["buttonSave"]}
            </ButtonBase>
          </Toolbar>
        </AppBar>
        <div className="upload-image-wrapper" style={{ flexGrow: 1 }}>
          <PictureEditor
            imagePath={imagePath}
            pictureEditorElement={() => pictureEditorElement}
            zoomInButton={zoomInButton}
            zoomOutButton={zoomOutButton}
            rotateLeftButton={rotateLeftButton}
            rotateRightButton={rotateRightButton}
          />
        </div>
        <div className="upload-image-action">
          {isUploadError && (
            <div
              className="error-message"
              dangerouslySetInnerHTML={{
                __html: langPack["ErrorUploadMessage"]
                  ? langPack["ErrorUploadMessage"]
                  : "Please reupload the image and <br/> try saving it again",
              }}
            />
          )}
          <ButtonBase
            className="upload-image__button upload-image__button--camera"
            onClick={selectImageHandler}
          />

          {imageDialogIndex !== "p1" && pictures[imageDialogIndex] && (
            <ButtonBase
              className="upload-image__button upload-image__button--delete"
              onClick={() => deleteImageHandler(imageDialogIndex)}
            />
          )}

          <ButtonBase
            className="upload-image__button upload-image__button--plus"
            ref={zoomInButton}
          />

          <ButtonBase
            className="upload-image__button upload-image__button--minus"
            ref={zoomOutButton}
          />

          <ButtonBase
            className="upload-image__button upload-image__button--rotate-left"
            ref={rotateLeftButton}
          />

          <ButtonBase
            className="upload-image__button upload-image__button--rotate-right"
            ref={rotateRightButton}
          />

          <canvas
            ref={canvasResizeProxy}
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              width: 1,
              height: 1,
              zIndex: -100,
            }}
          />
        </div>
        {isLoading && <Loader />}
      </Dialog>
    </React.Fragment>
  );
};

export default ArtworkPictures;